import { useEffect, useRef, useState } from 'react';
import { useAppContext } from '@/context/AppContext';

export function HistoryFeed() {
  const { state, dispatch, getHistory, getCurrentIndex } = useAppContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showChangeTooltip, setShowChangeTooltip] = useState(false);
  const [isTooltipDismissed, setIsTooltipDismissed] = useState(() => {
    return window.localStorage.getItem('historyChangeTooltipDismissed') === 'true';
  });
  const listRef = useRef<HTMLDivElement | null>(null);
  const hasShownChangeTooltipRef = useRef(false);
  const history = getHistory();
  const currentIndex = getCurrentIndex();

  const handleUndo = () => dispatch({ type: 'UNDO' });
  const handleRedo = () => dispatch({ type: 'REDO' });
  const handleJumpToState = (index: number) => dispatch({ type: 'JUMP_TO_STATE', payload: index });

  const renderDescription = (description: string): JSX.Element => {
    if (!description) return <span className="history-description">No changes detected</span>;

    const parts = description.split(' | ');
    return (
      <>
        {parts.map((part, i) => {
          const isAdded = part.includes(' added ');
          const colorClass = isAdded ? 'history-change-added' : 'history-change-removed';
          return (
            <span key={i} className={colorClass}>
              {i > 0 && ' | '}
              {part}
            </span>
          );
        })}
      </>
    );
  };

  const latestHistory = history.slice(-5).reverse();

  useEffect(() => {
    if (!isExpanded) return;
    const listElement = listRef.current;
    if (!listElement) return;
    listElement.scrollTop = listElement.scrollHeight;
  }, [history.length, isExpanded]);

  useEffect(() => {
    if (hasShownChangeTooltipRef.current) return;
    if (isTooltipDismissed) return;
    if (history.length === 0) return;
    const latestEntry = history[history.length - 1];
    const description = latestEntry?.description || '';
    if (!description.includes(' added ') && !description.includes(' removed ')) return;
    if (isExpanded) return;

    hasShownChangeTooltipRef.current = true;
    setShowChangeTooltip(true);

    const timeoutId = window.setTimeout(() => {
      setShowChangeTooltip(false);
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [history, isExpanded, isTooltipDismissed]);

  const handleDismissTooltip = () => {
    window.localStorage.setItem('historyChangeTooltipDismissed', 'true');
    hasShownChangeTooltipRef.current = true;
    setIsTooltipDismissed(true);
    setShowChangeTooltip(false);
  };

  return (
    <div className={`history-feed ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <button
        className="history-feed-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? 'Collapse history' : 'Expand history'}
      >
        🕐
      </button>

      {!isExpanded && showChangeTooltip && (
        <div className="history-feed-tooltip">
          <span>History updated</span>
          <button
            className="history-feed-tooltip-close"
            onClick={handleDismissTooltip}
            title="Dismiss"
            aria-label="Dismiss history tooltip"
          >
            ×
          </button>
        </div>
      )}

      <button
        className="history-feed-undo-collapsed"
        onClick={handleUndo}
        disabled={!state.ui.canUndo}
        title="Undo (Ctrl+Z)"
      >
        ↶
      </button>

      <div className="history-feed-content">
        <div className="history-feed-header">
          <h4>History</h4>
          <div className="history-feed-controls">
            <button
              onClick={handleUndo}
              disabled={!state.ui.canUndo}
              className="undo-btn"
              title="Undo (Ctrl+Z)"
            >
              ↶
            </button>
            <button
              onClick={handleRedo}
              disabled={!state.ui.canRedo}
              className="redo-btn"
              title="Redo (Ctrl+Y)"
            >
              ↷
            </button>
          </div>
        </div>

        <div className="history-feed-list" ref={listRef}>
          {history.length === 0 ? (
            <p className="history-feed-empty">No history available</p>
          ) : (
            <>
              {isExpanded ? (
                <ul className="history-list">
                  {history.map((entry) => (
                    <li key={entry.index} className={`history-item ${entry.index === currentIndex ? 'current' : ''}`}>
                      <div className="history-description">
                        {renderDescription(entry.description || '')}
                      </div>
                      <div className="history-meta">
                        <span className="history-timestamp">
                          {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {entry.index === currentIndex ? (
                          <span className="current-indicator">●</span>
                        ) : (
                          <button
                            onClick={() => handleJumpToState(entry.index)}
                            className="jump-to-btn"
                          >
                            ↶
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="history-list">
                  {latestHistory.map((entry) => (
                    <li key={entry.index} className={`history-item ${entry.index === currentIndex ? 'current' : ''}`}>
                      <div className="history-description">
                        {renderDescription(entry.description || '')}
                      </div>
                      <div className="history-meta">
                        <span className="history-timestamp">
                          {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {entry.index === currentIndex ? (
                          <span className="current-indicator">●</span>
                        ) : (
                          <button
                            onClick={() => handleJumpToState(entry.index)}
                            className="jump-to-btn"
                          >
                            ↶
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
